// /rankings/new and /rankings/:id/edit — create or edit a Custom Ranking:
// your own Top 5 / Top 10 of movies, TV series or both, under your own title.
// The size picker stays Top 5 / Top 10; under "Your Top N" the ranking can be
// expanded to a Top 15 or 20 (titles and order kept), and shrinking asks first,
// naming every title it would remove.
//
// Creating works without an account. Unsaved work lives in localStorage (one
// device, 7-day expiry — a convenience, not storage), so a refresh, a network
// error or the sign-up detour never loses it. Saving needs an account: a guest
// leaves through sign-in / sign-up (DOB and onboarding included) and comes back
// to /rankings/new?resume=1, where the draft is saved once — the draft id is
// sent as clientDraftId, so a repeated save returns the same ranking.
//
// Drafts and publishing: "Save draft" stores a private draft on the server
// (incomplete is fine: 6 of a Top 10). "Publish ranking" needs a title and
// every slot filled and makes it public. Once published, "Save changes"
// stores private edits and "Update published ranking" makes them live; the
// published version can be restored (discarding the edits) or unpublished.
//
// "Make Your Own" arrives as /rankings/new?from=<slug>: the source ranking's
// title and size are copied, never its titles, and the slug is sent so the
// server can record what inspired the new ranking.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RankingService, getRankingErrorMessage } from '../../services/rankingService';
import {
    MAX_RANKING_DESCRIPTION_LENGTH, MAX_RANKING_TITLE_LENGTH, RANKING_SIZES, RANKING_START_SIZES, addRankingItem,
    isRankingComplete, moveRankingItem, normalizeRankingDescription, normalizeRankingTitle, rankingComposition,
    rankingDraftFromSaved, rankingDraftProblems, rankingExpansionSizes, rankingFullNotice, rankingItemKey,
    rankingMatchesSaved, rankingOverflow, rankingPayload, rankingProblems, rankingTitleLength,
    removeRankingItem, replaceRankingItem, setRankingSize,
} from '@aluvo/constants/src/rankings';
import {
    clearRankingDraft, freshRankingDraft, hasDraftContent, loadRankingDraft, saveRankingDraft,
} from '../../utils/rankingDraft';
import { clearPendingIntent, savePendingIntent } from '../../utils/pendingIntent';
import { setReferral } from '../../attribution/referral';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { trackRanking } from '../../analytics/rankingAnalytics';
import { useShareMetadata } from '../../utils/shareMetadata';
import { PosterImage } from '../../components/TopMovies/TopMoviesGrid';
import TopMoviesGrid from '../../components/TopMovies/TopMoviesGrid';
import RankSlots from '../../components/RankSlots/RankSlots';
import RankingSharePanel from '../../components/Rankings/RankingSharePanel';
import base from '../TopMovies/TopMovies.module.css';
import styles from './RankingEditor.module.css';

const SEARCH_DEBOUNCE_MS = 350;
const RESUME_INTENT_MS = 2 * 60 * 60 * 1000;
const RESUME_PATH = '/rankings/new?resume=1';
const KIND_FILTERS = [
    { key: '', label: 'All' },
    { key: 'movie', label: 'Movies' },
    { key: 'tv', label: 'TV shows' },
];
export const TITLE_SUGGESTIONS = [
    'My Top 5 Comedies',
    'My Top 5 Anime Series',
    'My Top 10 TV Shows',
    'My Top 10 Horror Movies',
    'My Top 10 Greatest Stories Ever Told',
];

const kindLabel = (kind) => (kind === 'tv' ? 'TV' : 'Movie');
const metaOf = (item) => [item.year, kindLabel(item.kind)].filter(Boolean).join(' · ');

function SearchResults({ results, draft, replacingIndex, onPick }) {
    const full = draft.items.length >= draft.size && replacingIndex === null;
    const picked = new Set(draft.items.map(rankingItemKey));
    return (
        <ul className={base.results} aria-label="Search results">
            {results.map((media) => {
                const key = rankingItemKey(media);
                const already = picked.has(key);
                return (
                    <li key={key}>
                        <button
                            type="button"
                            className={`${base.result} ${already ? base.resultPicked : ''}`}
                            onClick={() => onPick(media)}
                            disabled={already || full}
                            aria-label={already
                                ? `${media.title} is already in your ranking`
                                : `${replacingIndex !== null ? `Put ${media.title} at #${replacingIndex + 1}` : `Add ${media.title}`}${media.year ? ` (${media.year})` : ''}, ${media.kind === 'tv' ? 'TV series' : 'movie'}`}
                        >
                            <PosterImage movie={media} className={base.resultPoster} />
                            <span className={base.resultText}>
                                <strong>{media.title}<span className={styles.kindBadge}>{kindLabel(media.kind)}</span></strong>
                                <span>{media.year || '—'}</span>
                            </span>
                            <span className={base.resultAction} aria-hidden="true">{already ? '✓' : '+'}</span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

/** The status line over the editor's actions: where the ranking stands and whether it is saved. */
function SaveState({ status, unpublishedChanges, dirty, saving, savedNote }) {
    let text;
    if (saving) text = 'Saving…';
    else if (dirty) text = status === 'new' ? 'Not saved yet — kept on this device.' : 'Unsaved changes';
    else if (savedNote) text = savedNote;
    else if (status === 'draft') text = 'Only you can see this draft.';
    else if (status === 'published') text = unpublishedChanges ? 'Your saved changes are private until you update the published ranking.' : 'Live — this is the version people see.';
    else text = 'Not saved yet — kept on this device.';
    return (
        <div className={styles.saveState} data-testid="ranking-save-state">
            <span className={`${styles.statusBadge} ${status === 'published' ? styles.statusLive : ''}`}>
                {status === 'published' ? 'Published' : status === 'draft' ? 'Draft' : 'New ranking'}
            </span>
            {status === 'published' && unpublishedChanges && <span className={styles.changesBadge}>Unpublished changes</span>}
            <span className={styles.saveStateText} role="status" aria-live="polite">{text}</span>
        </div>
    );
}

export default function RankingEditor() {
    const { id: editId } = useParams();
    const isEdit = Boolean(editId);
    const { user, loading: authLoading } = useAuth();
    const loggedIn = Boolean(user);
    const navigate = useNavigate();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const fromSlug = params.get('from');
    const resume = params.get('resume') === '1';

    const [draft, setDraft] = useState(() => (isEdit ? null : loadRankingDraft() || freshRankingDraft()));
    const [saved, setSaved] = useState(isEdit ? location.state?.ranking ?? null : null);
    const [ready, setReady] = useState(false);
    const [loadError, setLoadError] = useState('');
    // 'draft' | 'published' after the create → edit hand-off (true from older history entries).
    const [justCreated] = useState(location.state?.justCreated || null);
    const [sourceTitle, setSourceTitle] = useState(null);
    const [myoPrompt, setMyoPrompt] = useState(null); // a Make Your Own draft waiting to replace a local one

    const [query, setQuery] = useState('');
    const [kindFilter, setKindFilter] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [notice, setNotice] = useState('');
    const [replacingIndex, setReplacingIndex] = useState(null);
    const searchTicket = useRef(0);

    const [pendingSize, setPendingSize] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(location.state?.publishError || '');
    const [savedNote, setSavedNote] = useState('');
    // The newer server version when someone else saved first, and what we were doing.
    const [conflict, setConflict] = useState(null); // { latest, action: 'save' | 'publish' }
    const [problemsFor, setProblemsFor] = useState(null); // 'draft' | 'publish': which rules to list
    const savingRef = useRef(false);
    const startedRef = useRef(false);
    const createdRef = useRef(false); // set once saved, so the cleared draft is never written back

    useShareMetadata(useMemo(() => ({
        title: isEdit ? 'Edit ranking | Aluvo' : 'Make a Ranking — Your Top 5 or Top 10 | Aluvo',
        description: 'Rank your favorite movies and TV shows — a Top 5 or Top 10, up to a Top 20, under your own title — and share it.',
        url: `${window.location.origin}/rankings/new`,
    }), [isEdit]));

    const source = draft?.sourceSlug ? 'make_your_own' : (location.state?.source || 'direct');
    const analyticsBase = useCallback((d) => ({
        size: d.size,
        composition: rankingComposition(d.items) || 'none',
        logged_in: loggedIn,
        source,
    }), [loggedIn, source]);

    // ── Create: saving (signed in) ─────────────────────────────────────────
    // A new ranking is saved as a private draft (it may be incomplete). With
    // `publish`, the saved draft is then published; if that step fails the
    // draft is kept and the editor says why.
    const createRanking = useCallback(async (d, { wasGuest = false, publish = false } = {}) => {
        if (savingRef.current) return null;
        savingRef.current = true;
        setSaving(true);
        setSaveError('');
        try {
            const { ranking: made, created } = await RankingService.create(rankingPayload(d));
            createdRef.current = true;
            clearRankingDraft();
            clearPendingIntent();
            let ranking = made;
            let publishError = '';
            if (publish && made.status !== 'published') {
                try {
                    ranking = (await RankingService.publish(made.id)).ranking;
                } catch (err) {
                    publishError = getRankingErrorMessage(err, "Your draft is saved, but it couldn't be published. Try Publish ranking again.");
                }
            }
            // A repeat of an already-saved draft (double resume, retry) returns the
            // same ranking with created: false — never counted twice.
            if (created) {
                const props = { ...analyticsBase(d), composition: ranking.composition || 'none', visibility: ranking.visibility, was_guest: wasGuest };
                trackRanking(ANALYTICS_EVENTS.RANKING_CREATED, props, { listSlug: ranking.shareSlug || null });
                if (d.sourceSlug) trackRanking(ANALYTICS_EVENTS.RANKING_MAKE_YOUR_OWN_COMPLETED, { ...props, ref: d.sourceSlug }, { listSlug: d.sourceSlug });
            }
            navigate(`/rankings/${ranking.id}/edit`, { replace: true, state: { justCreated: ranking.status, ranking, publishError } });
            return ranking;
        } catch (err) {
            setSaveError(getRankingErrorMessage(err, "Couldn't save your ranking. It's kept on this device — try again."));
            return null;
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [analyticsBase, navigate]);

    // ── Load: edit an existing ranking ─────────────────────────────────────
    useEffect(() => {
        if (!isEdit || authLoading) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { ranking } = await RankingService.getMine(editId);
                if (cancelled) return;
                setSaved(ranking);
                setDraft(rankingDraftFromSaved(ranking));
            } catch (err) {
                if (!cancelled) setLoadError(err?.statusCode === 404 ? 'This ranking was not found. It may have been deleted.' : getRankingErrorMessage(err, "Couldn't load this ranking."));
            } finally {
                if (!cancelled) setReady(true);
            }
        })();
        return () => { cancelled = true; };
    }, [isEdit, editId, authLoading]);

    // Top of the creation funnel: once per page load, once auth is known. The
    // return from sign-up (?resume=1) is the same visit, so it isn't counted again.
    useEffect(() => {
        if (isEdit || authLoading || resume) return;
        trackRanking(
            ANALYTICS_EVENTS.RANKING_EDITOR_OPENED,
            { logged_in: loggedIn, source: fromSlug ? 'make_your_own' : (location.state?.source || 'direct'), ...(fromSlug ? { ref: fromSlug } : {}) },
            { onceKey: 'page' },
        );
        // Fires once per mount; later auth or location changes are not new visits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, authLoading]);

    // ── Load: new ranking (Make Your Own, resume after sign-in) ────────────
    useEffect(() => {
        if (isEdit || authLoading) return undefined;
        let cancelled = false;
        (async () => {
            const local = loadRankingDraft();
            if (fromSlug) {
                try {
                    const shared = await RankingService.getShared(fromSlug);
                    if (cancelled) return;
                    if (shared && !shared.isPrivate) {
                        if (!loggedIn) setReferral('ranking', shared.slug);
                        setSourceTitle(shared.ranking.title);
                        const fresh = freshRankingDraft({ title: shared.ranking.title, size: shared.ranking.size, sourceSlug: shared.slug });
                        if (local && hasDraftContent(local) && local.sourceSlug !== shared.slug) setMyoPrompt(fresh);
                        else if (!local || local.sourceSlug !== shared.slug) setDraft(fresh);
                    } else {
                        setNotice("That ranking isn't shared anymore — start your own below.");
                    }
                } catch {
                    if (!cancelled) setNotice("That ranking isn't available — start your own below.");
                }
            }
            if (!cancelled && resume && loggedIn && local?.pendingSave) {
                // Saved as the draft it is; published only if that was asked for and it's ready.
                const publish = local.pendingSave === 'publish' && isRankingComplete(local);
                if (rankingDraftProblems(local).length === 0) {
                    setDraft(local);
                    await createRanking(local, { wasGuest: true, publish });
                } else {
                    setDraft({ ...local, pendingSave: false });
                    setProblemsFor('draft');
                }
            }
            if (!cancelled) setReady(true);
        })();
        return () => { cancelled = true; };
        // Runs once auth is known; createRanking is stable for that purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, authLoading, fromSlug, resume, loggedIn]);

    // Keep a new ranking's work on this device (edits of saved rankings stay in memory).
    useEffect(() => {
        if (isEdit || !draft || !ready || createdRef.current) return;
        if (hasDraftContent(draft)) saveRankingDraft(draft);
    }, [draft, isEdit, ready]);

    // Warn before leaving with unsaved edits to a saved ranking (a new one is kept on the device).
    const dirty = isEdit && draft && saved ? !rankingMatchesSaved(draft, saved) : false;
    useEffect(() => {
        if (!dirty) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // ── Search (movies, TV or both); stale responses are dropped ───────────
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setSearching(false);
            setSearchError('');
            return undefined;
        }
        setSearching(true);
        const ticket = ++searchTicket.current;
        const timer = setTimeout(async () => {
            try {
                const data = await RankingService.searchTitles(q, { kind: kindFilter || undefined });
                if (ticket !== searchTicket.current) return;
                setResults((data.results || []).filter((m) => m.tmdbId && (m.kind === 'movie' || m.kind === 'tv')).slice(0, 12));
                setSearchError('');
            } catch (err) {
                if (ticket !== searchTicket.current) return;
                setSearchError(getRankingErrorMessage(err, "Search isn't working right now. Please try again."));
            } finally {
                if (ticket === searchTicket.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, kindFilter]);

    const update = (next) => {
        setDraft(next);
        setSaveError('');
        setSavedNote('');
        if (!isEdit && !startedRef.current && hasDraftContent(next)) {
            startedRef.current = true;
            trackRanking(ANALYTICS_EVENTS.RANKING_CREATION_STARTED, { size: next.size, logged_in: loggedIn, source }, { onceKey: next.draftId || 'draft' });
        }
    };

    function handlePick(media) {
        if (replacingIndex !== null) {
            const { draft: next, status } = replaceRankingItem(draft, replacingIndex, media);
            if (status === 'duplicate') return setNotice(`${media.title} is already in your ranking.`);
            setNotice(`${media.title} is now #${replacingIndex + 1}.`);
            setReplacingIndex(null);
            return update(next);
        }
        const { draft: next, status } = addRankingItem(draft, media);
        if (status === 'duplicate') return setNotice(`${media.title} is already in your ranking.`);
        if (status === 'full') return setNotice(rankingFullNotice(draft.size));
        setNotice(`Added ${media.title} at #${next.items.length}.`);
        return update(next);
    }

    function requestSize(size) {
        if (size === draft.size) return;
        // Growing never loses anything; shrinking over picked titles asks first.
        if (rankingOverflow(draft, size).length) setPendingSize(size);
        else {
            update(setRankingSize(draft, size));
            if (size > draft.size) setNotice(`Now a Top ${size} — your ${draft.items.length} ${draft.items.length === 1 ? 'pick stays' : 'picks stay'} in order. Add ${size - draft.items.length} more.`);
        }
    }

    function confirmSize() {
        update(setRankingSize(draft, pendingSize));
        setReplacingIndex(null);
        setPendingSize(null);
    }

    // ── Guest: leave to sign in / sign up, come back and save once ─────────
    function leaveToAuth(path, intent = 'draft') {
        saveRankingDraft({ ...draft, pendingSave: intent === 'publish' ? 'publish' : true });
        savePendingIntent({ type: 'SAVE_RANKING', returnUrl: RESUME_PATH }, { expiresAt: Date.now() + RESUME_INTENT_MS });
        trackEvent(ANALYTICS_EVENTS.GUEST_AUTH_GATE_SHOWN, { platform: 'web', reason: 'ranking' });
        trackRanking(
            ANALYTICS_EVENTS.RANKING_SIGNUP_STARTED,
            { ...analyticsBase(draft), action: path === '/register' ? 'sign_up' : 'log_in', ...(draft.sourceSlug ? { ref: draft.sourceSlug } : {}) },
            { onceKey: draft.draftId || 'draft' },
        );
        navigate(path, { state: { from: { pathname: '/rankings/new', search: '?resume=1' } } });
    }

    /** Run one owner request with the saving guard; failures keep the edits on screen. */
    async function guarded(run, fallback) {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setSaveError('');
        setSavedNote('');
        try {
            await run();
        } catch (err) {
            setSaveError(err?.statusCode === 404
                ? 'This ranking no longer exists — it may have been deleted elsewhere.'
                : getRankingErrorMessage(err, fallback));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }

    function applySaved(ranking) {
        setSaved(ranking);
        setDraft(rankingDraftFromSaved(ranking));
        setConflict(null);
        setReplacingIndex(null);
        setProblemsFor(null);
    }

    // ── Edit: save or publish, after a check for someone else's newer save ─
    async function commitEdit(action, { overwrite = false } = {}) {
        await guarded(async () => {
            if (!overwrite) {
                const { ranking: latest } = await RankingService.getMine(editId);
                if (latest.updatedAt !== saved.updatedAt) {
                    setConflict({ latest, action });
                    return;
                }
            }
            const wasPublished = saved.status === 'published';
            const changed = [
                normalizeRankingTitle(draft.title) !== saved.title && 'title',
                (draft.description || '') !== (saved.description || '') && 'description',
                draft.size !== saved.size && 'size',
                (draft.showPositions !== false) !== (saved.showPositions !== false) && 'positions',
                (draft.items.length !== saved.items.length || draft.items.some((item, i) => rankingItemKey(item) !== rankingItemKey(saved.items[i] || {}))) && 'items',
            ].filter(Boolean);
            const body = rankingPayload(draft, { forCreate: false });
            const { ranking } = action === 'publish'
                ? await RankingService.publish(editId, body)
                : await RankingService.replace(editId, body);
            applySaved(ranking);
            if (action === 'publish') setSavedNote(wasPublished ? 'Published ranking updated ✓ — your changes are live.' : 'Published ✓ — anyone with the link can see it.');
            else setSavedNote(ranking.status === 'published' ? 'Changes saved ✓ — not live until you update the published ranking.' : 'Draft saved ✓');
            trackEvent(ANALYTICS_EVENTS.RANKING_EDITED, { size: ranking.size, composition: ranking.composition || 'none', visibility: ranking.visibility, changed: [...changed, action === 'publish' && 'published'].filter(Boolean).join(',') || 'none' });
        }, action === 'publish' ? "Couldn't publish your ranking. Your edits are still here — try again." : "Couldn't save your changes. They're still here — try again.");
    }

    async function handleSaveDraft() {
        if (rankingDraftProblems(draft).length) {
            setProblemsFor('draft');
            return;
        }
        if (isEdit) await commitEdit('save');
        else await createRanking(draft);
    }

    async function handlePublish() {
        // Explain what is missing; nothing is filled in or trimmed to make it fit.
        if (!isRankingComplete(draft)) {
            setProblemsFor('publish');
            return;
        }
        if (isEdit) await commitEdit('publish');
        else await createRanking(draft, { publish: true });
    }

    async function handleRestorePublished() {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Discard your unpublished changes? Your ranking goes back to the published version. This can\'t be undone.')) return;
        await guarded(async () => {
            const { ranking } = await RankingService.discardChanges(editId);
            applySaved(ranking);
            setSavedNote('Back to the published version ✓');
        }, "Couldn't restore the published version. Please try again.");
    }

    async function handleUnpublish() {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Unpublish "${saved.title}"? Its link and profile listing stop showing it. It stays here as a draft, and publishing it again brings back the same link.`)) return;
        await guarded(async () => {
            const { ranking } = await RankingService.unpublish(editId);
            // Unsaved edits on screen stay on screen; only the status changes.
            setSaved(ranking);
            setSavedNote('Unpublished ✓ — it is a private draft now.');
        }, "Couldn't unpublish this ranking. Please try again.");
    }

    async function handleDelete() {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete "${saved.title || 'this draft'}"? ${saved.shareUrl ? 'Its share link will stop working. ' : ''}This can't be undone.`)) return;
        try {
            await RankingService.remove(editId);
            navigate('/me/rankings', { replace: true, state: { deleted: saved.title || 'Untitled draft' } });
        } catch (err) {
            setSaveError(getRankingErrorMessage(err, "Couldn't delete this ranking. Please try again."));
        }
    }

    function startOver() {
        // eslint-disable-next-line no-alert
        if (hasDraftContent(draft) && !window.confirm('Clear this draft and start a new ranking?')) return;
        clearRankingDraft();
        startedRef.current = false;
        setReplacingIndex(null);
        setDraft(freshRankingDraft());
    }

    if (loadError) {
        return (
            <main className={base.page}>
                <div className={base.shell}>
                    <p className={base.errorBanner} role="alert">{loadError}</p>
                    <p><Link to="/me/rankings">← My Rankings</Link></p>
                </div>
            </main>
        );
    }
    if (!draft || (isEdit && !saved)) {
        return <main className={base.page}><div className={base.shell}><p className={base.muted} role="status">Loading ranking…</p></div></main>;
    }

    const complete = isRankingComplete(draft);
    const publishProblems = rankingProblems(draft);
    const problems = problemsFor === 'publish' ? publishProblems : problemsFor === 'draft' ? rankingDraftProblems(draft) : [];
    const titleLength = rankingTitleLength(normalizeRankingTitle(draft.title));
    const descriptionLength = rankingTitleLength(normalizeRankingDescription(draft.description || ''));
    const progressPct = Math.round((draft.items.length / draft.size) * 100);
    const shownTitle = normalizeRankingTitle(draft.title) || `My Top ${draft.size}`;
    const status = isEdit ? saved.status : 'new';
    const isPublished = status === 'published';
    const unpublishedChanges = isPublished && (Boolean(saved.hasUnpublishedChanges) || dirty);
    const liveVersion = isPublished ? saved.published : null;
    const busy = saving || Boolean(conflict);

    return (
        <main className={base.page}>
            <div className={base.glow} aria-hidden="true" />
            <div className={base.shell}>
                <header className={base.hero}>
                    <span className={base.eyebrow}>{isEdit ? 'Edit ranking' : 'Aluvo · Rankings'}</span>
                    <h1>{isEdit ? 'Edit your ranking' : 'Rank anything you love'} <span aria-hidden="true">🏆</span></h1>
                    <p>Your Top 5 or Top 10 — movies, TV shows or both. Expand it up to a Top 20 any time. Save a draft whenever you like; publish it when it&apos;s ready.</p>
                </header>

                {justCreated && saved && (
                    <section className={styles.successBanner} aria-live="polite">
                        {saved.status === 'published' ? (
                            <>
                                <strong>Published ✓ — “{saved.title}” is live.</strong>
                                <p>Share it below and see who makes their own.</p>
                            </>
                        ) : (
                            <>
                                <strong>Draft saved ✓ — {saved.title ? `“${saved.title}”` : 'your ranking'} is in My Rankings under Drafts.</strong>
                                <p>Only you can see it. Come back any time; publish it when it&apos;s ready.</p>
                            </>
                        )}
                    </section>
                )}

                {sourceTitle && !isEdit && (
                    <p className={styles.sourceNote}>Making your own version of “{sourceTitle}”. Pick your own titles — nothing is copied but the title and size.</p>
                )}

                {myoPrompt && (
                    <section className={base.prompt} role="alertdialog" aria-labelledby="myo-title">
                        <strong id="myo-title">Start a new ranking?</strong>
                        <p>You have an unsaved draft on this device. Starting “{myoPrompt.title}” replaces it.</p>
                        <div className={base.promptActions}>
                            <button type="button" className={base.primary} onClick={() => { setDraft(myoPrompt); startedRef.current = false; setMyoPrompt(null); }}>Start new ranking</button>
                            <button type="button" className={base.secondary} onClick={() => { setMyoPrompt(null); setSourceTitle(null); }}>Keep my draft</button>
                        </div>
                    </section>
                )}

                <section className={base.controls} aria-label="Ranking options">
                    <div className={styles.titleField}>
                        <label className={styles.titleLabel} htmlFor="ranking-title">Ranking title</label>
                        <input
                            id="ranking-title"
                            className={styles.titleInput}
                            type="text"
                            placeholder="e.g. My Top 5 Anime Series"
                            value={draft.title}
                            maxLength={MAX_RANKING_TITLE_LENGTH + 20}
                            onChange={(e) => update({ ...draft, title: e.target.value })}
                            aria-describedby="ranking-title-count"
                            aria-invalid={titleLength > MAX_RANKING_TITLE_LENGTH || undefined}
                            autoComplete="off"
                        />
                        <div className={styles.titleMeta}>
                            <span>Any title you like — titles don&apos;t have to match a genre.</span>
                            <span id="ranking-title-count" className={titleLength > MAX_RANKING_TITLE_LENGTH ? styles.titleOver : undefined}>
                                {titleLength}/{MAX_RANKING_TITLE_LENGTH}
                            </span>
                        </div>
                        {!isEdit && !draft.title.trim() && (
                            <ul className={styles.chips} aria-label="Title ideas">
                                {TITLE_SUGGESTIONS.map((suggestion) => (
                                    <li key={suggestion}>
                                        <button type="button" className={styles.chip} onClick={() => update({ ...draft, title: suggestion, size: suggestion.includes('Top 10') ? 10 : draft.size })}>{suggestion}</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <label className={styles.titleLabel} htmlFor="ranking-description">Description <span className={base.muted}>(optional)</span></label>
                        <textarea
                            id="ranking-description"
                            className={styles.descriptionInput}
                            placeholder="What ties your picks together?"
                            value={draft.description || ''}
                            rows={2}
                            maxLength={MAX_RANKING_DESCRIPTION_LENGTH + 50}
                            onChange={(e) => update({ ...draft, description: e.target.value })}
                            aria-describedby="ranking-description-count"
                            aria-invalid={descriptionLength > MAX_RANKING_DESCRIPTION_LENGTH || undefined}
                        />
                        <div className={styles.titleMeta}>
                            <span>Shown on your ranking&apos;s page.</span>
                            <span id="ranking-description-count" className={descriptionLength > MAX_RANKING_DESCRIPTION_LENGTH ? styles.titleOver : undefined}>
                                {descriptionLength}/{MAX_RANKING_DESCRIPTION_LENGTH}
                            </span>
                        </div>
                    </div>
                    <div className={base.segmented} role="group" aria-label="Ranking size">
                        {/* Top 5 / Top 10 to start; an expanded size joins once chosen so it can be shrunk back. */}
                        {RANKING_SIZES.filter((size) => RANKING_START_SIZES.includes(size) || size <= draft.size).map((size) => (
                            <button key={size} type="button" aria-pressed={draft.size === size} onClick={() => requestSize(size)}>Top {size}</button>
                        ))}
                    </div>
                    <div className={base.progress} aria-live="polite">
                        <span><strong>{draft.items.length} of {draft.size}</strong> selected</span>
                        <span className={base.track} aria-hidden="true"><span style={{ width: `${progressPct}%` }} /></span>
                    </div>
                </section>

                {pendingSize && (
                    <section className={base.prompt} role="alertdialog" aria-labelledby="size-title">
                        <strong id="size-title">Shrink to a Top {pendingSize}?</strong>
                        <p>
                            This removes {rankingOverflow(draft, pendingSize).length === 1 ? 'the title' : `the ${rankingOverflow(draft, pendingSize).length} titles`} ranked
                            {' '}#{pendingSize + 1}{draft.items.length > pendingSize + 1 ? `–#${draft.items.length}` : ''}:{' '}
                            {rankingOverflow(draft, pendingSize).map((item) => item.title).join(', ')}.
                            {isEdit ? ' Nothing changes until you save.' : ''}
                        </p>
                        <div className={base.promptActions}>
                            <button type="button" className={base.primary} onClick={confirmSize}>Remove them and switch</button>
                            <button type="button" className={base.secondary} onClick={() => setPendingSize(null)}>Keep my Top {draft.size}</button>
                        </div>
                    </section>
                )}

                <div className={base.workspace}>
                    <section className={base.searchCol} aria-label="Find movies and TV shows">
                        <label className={base.searchLabel} htmlFor="ranking-search">Search movies and TV shows</label>
                        <div className={base.segmented} role="group" aria-label="Search in">
                            {KIND_FILTERS.map((f) => (
                                <button key={f.key || 'all'} type="button" aria-pressed={kindFilter === f.key} onClick={() => setKindFilter(f.key)}>{f.label}</button>
                            ))}
                        </div>
                        <input
                            id="ranking-search"
                            className={base.searchInput}
                            type="search"
                            placeholder="e.g. Breaking Bad, The Dark Knight"
                            autoComplete="off"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {replacingIndex !== null && draft.items[replacingIndex] && (
                            <p className={styles.replacing} aria-live="polite">
                                Pick a title to replace #{replacingIndex + 1} {draft.items[replacingIndex].title}.{' '}
                                <button type="button" className={base.linkButton} onClick={() => setReplacingIndex(null)}>Cancel</button>
                            </p>
                        )}
                        <p className={base.notice} aria-live="polite">{notice}</p>
                        {searching && <p className={base.muted} role="status">Searching…</p>}
                        {searchError && <p className={base.errorText} role="alert">{searchError}</p>}
                        {!searching && !searchError && query.trim().length >= 2 && results.length === 0 && <p className={base.muted}>Nothing found. Try another title.</p>}
                        {results.length > 0 && <SearchResults results={results} draft={draft} replacingIndex={replacingIndex} onPick={handlePick} />}
                    </section>

                    <section className={base.listCol} aria-label={`Your Top ${draft.size}`}>
                        <div className={base.listHead}>
                            <h2>Your Top {draft.size}</h2>
                            <span className={base.muted}>Drag ⋮⋮ or use the arrows to rank · ⇄ replaces a title</span>
                        </div>
                        {rankingExpansionSizes(draft.size).length > 0 && (
                            <div className={styles.expand} role="group" aria-label="Make your ranking longer">
                                <span>Want more room? Expand to</span>
                                {rankingExpansionSizes(draft.size).map((size) => (
                                    <button key={size} type="button" className={styles.expandButton} onClick={() => requestSize(size)} aria-label={`Expand to a Top ${size}`}>Top {size}</button>
                                ))}
                                <span className={base.muted}>Your picks and their order stay.</span>
                            </div>
                        )}
                        <RankSlots
                            items={draft.items}
                            size={draft.size}
                            label={`Your Top ${draft.size}`}
                            keyOf={rankingItemKey}
                            metaOf={metaOf}
                            emptyHint={(index, count) => (index === count ? 'Search for a movie or show to add it here' : 'Empty')}
                            replacingIndex={replacingIndex}
                            onReplace={(index) => setReplacingIndex((current) => (current === index ? null : index))}
                            onRemove={(item, index) => { setReplacingIndex(null); update(removeRankingItem(draft, index)); }}
                            onMove={(from, to) => { setReplacingIndex(null); update(moveRankingItem(draft, from, to)); }}
                        />
                    </section>
                </div>

                {draft.items.length > 0 && (
                    <section className={styles.preview} aria-label="Preview">
                        <h2 className={styles.previewTitle}>{shownTitle}</h2>
                        {normalizeRankingDescription(draft.description || '') && <p className={styles.previewDescription}>{normalizeRankingDescription(draft.description)}</p>}
                        <TopMoviesGrid movies={draft.items} size={draft.size} ranked={draft.showPositions !== false} label="Preview, in your order" linkMovies={false} />
                    </section>
                )}

                <section className={base.saveBar} aria-label="Save and publish">
                    <SaveState status={status} unpublishedChanges={unpublishedChanges} dirty={dirty} saving={saving} savedNote={savedNote} />
                    <label className={base.toggle}>
                        <input type="checkbox" checked={draft.showPositions !== false} onChange={(e) => update({ ...draft, showPositions: e.target.checked })} />
                        <span>
                            <strong>Show position numbers</strong>
                            <span>Show 1, 2, 3… on your ranking&apos;s page. Turn off to show the titles without numbers — your order is saved either way. Share images have their own setting.</span>
                        </span>
                    </label>
                    {problems.length > 0 && (
                        <ul className={styles.problems} role="alert">
                            {problems.map((p) => <li key={p}>{p}</li>)}
                            {problemsFor === 'publish' && <li className={styles.problemsNote}>You can still save it as a draft and finish later.</li>}
                        </ul>
                    )}
                    {conflict && (
                        <div className={base.prompt} role="alertdialog" aria-labelledby="conflict-title">
                            <strong id="conflict-title">This ranking changed somewhere else.</strong>
                            <p>It was saved from another tab or device after you opened it. Keep your version, or load the latest?</p>
                            <div className={base.promptActions}>
                                <button type="button" className={base.primary} onClick={() => commitEdit(conflict.action, { overwrite: true })} disabled={saving}>Save my version</button>
                                <button type="button" className={base.secondary} onClick={() => applySaved(conflict.latest)}>Load the latest</button>
                            </div>
                        </div>
                    )}
                    {loggedIn || isEdit ? (
                        <div className={base.promptActions}>
                            <button
                                type="button"
                                className={base.secondary}
                                onClick={handleSaveDraft}
                                disabled={busy || (isEdit && !dirty)}
                            >
                                {saving ? 'Saving…' : isEdit && !dirty ? 'Saved ✓' : isPublished ? 'Save changes' : 'Save draft'}
                            </button>
                            <button
                                type="button"
                                className={base.primary}
                                onClick={handlePublish}
                                disabled={busy || (isPublished && !unpublishedChanges)}
                                aria-describedby={!complete ? 'ranking-publish-hint' : undefined}
                            >
                                {isPublished ? 'Update published ranking' : 'Publish ranking'}
                            </button>
                            {isEdit && dirty && (
                                <button type="button" className={base.ghost} onClick={() => { setDraft(rankingDraftFromSaved(saved)); setReplacingIndex(null); setSaveError(''); setProblemsFor(null); }}>Undo unsaved edits</button>
                            )}
                            {isPublished && saved.hasUnpublishedChanges && (
                                <button type="button" className={base.ghost} onClick={handleRestorePublished} disabled={busy}>Discard changes and restore published version</button>
                            )}
                            {isPublished && <Link className={base.ghost} to={`/rankings/${saved.shareSlug}`}>View public page</Link>}
                            {!isEdit && <button type="button" className={base.ghost} onClick={startOver}>Start over</button>}
                        </div>
                    ) : (
                        !authLoading && (
                            <div className={base.signupCard}>
                                <div>
                                    <h2>Save it as a draft, or publish it with a link to share.</h2>
                                    <p>Create a free account to save your ranking. Your picks stay on this device while you sign up.</p>
                                </div>
                                <div className={base.promptActions}>
                                    <button type="button" className={base.primary} onClick={() => (rankingDraftProblems(draft).length ? setProblemsFor('draft') : leaveToAuth('/register'))}>Create a Free Account to Save</button>
                                    <button type="button" className={base.ghost} onClick={() => (rankingDraftProblems(draft).length ? setProblemsFor('draft') : leaveToAuth('/login'))}>I have an account</button>
                                </div>
                            </div>
                        )
                    )}
                    {!complete && !problems.length && <p id="ranking-publish-hint" className={base.muted}>{publishProblems[0]} You can save a draft any time.</p>}
                    {saveError && <p className={base.errorText} role="alert">{saveError}</p>}
                </section>

                {isEdit && !dirty && (isPublished || draft.items.length > 0) && (
                    <section className={base.panel} aria-label="Share">
                        <h2 className={base.panelTitle}>{isPublished ? `Share your Top ${liveVersion.size}` : 'Preview your share image'}</h2>
                        {isPublished ? (
                            <>
                                {saved.hasUnpublishedChanges && <p className={base.muted}>This shares your published version. Update the published ranking to share your latest changes.</p>}
                                <RankingSharePanel
                                    items={liveVersion.items}
                                    size={liveVersion.size}
                                    composition={liveVersion.composition}
                                    showPositions={liveVersion.showPositions !== false}
                                    shared={{ slug: saved.shareSlug, shareUrl: saved.shareUrl, title: liveVersion.title, ownerUsername: user?.username || null }}
                                    storageId={saved.id}
                                    loggedIn={loggedIn}
                                    surface="editor"
                                />
                            </>
                        ) : (
                            <>
                                <p className={base.muted}>Only you can see this preview. Publish your ranking to get a link and share it.</p>
                                <RankingSharePanel
                                    items={draft.items}
                                    size={draft.size}
                                    composition={rankingComposition(draft.items)}
                                    showPositions={draft.showPositions !== false}
                                    privateCard={{ id: saved.id, title: saved.title, ownerUsername: user?.username || null }}
                                    storageId={saved.id}
                                    loggedIn={loggedIn}
                                    surface="editor"
                                    noLinkHint="Publish your ranking to get a link to share."
                                />
                            </>
                        )}
                    </section>
                )}

                {!isEdit && !loggedIn && complete && (
                    <section className={base.panel} aria-label="Share">
                        <h2 className={base.panelTitle}>Share your Top {draft.size}</h2>
                        <RankingSharePanel
                            items={draft.items}
                            size={draft.size}
                            composition={rankingComposition(draft.items)}
                            showPositions={draft.showPositions !== false}
                            loggedIn={loggedIn}
                            surface="editor"
                            noLinkHint="This preview has no title. Publish your ranking to share it with its title and a link."
                        />
                    </section>
                )}

                {isEdit && (
                    <p className={base.deleteRow}>
                        <Link to="/me/rankings">← My Rankings</Link>
                        {isPublished && (
                            <>
                                {' · '}
                                <button type="button" className={base.linkButton} onClick={handleUnpublish} disabled={busy}>Unpublish</button>
                            </>
                        )}
                        {' · '}
                        <button type="button" className={base.linkButton} onClick={handleDelete}>Delete this ranking</button>
                    </p>
                )}
            </div>
        </main>
    );
}
